import { useState } from "react";
import { PLATAFORMAS } from "../constants";
import { Btn, Modal, Field, Inp, Sel, Txta, FormRow, Stage, Chips, Selo, ExpandTable, Detalhe } from "../components/primitives";

const ROTEIRO_TONE = { Ativo: "info", Estrela: "gold", Arquivado: "off" };

function RoteiroModal({ initial, clients, onClose, onSave, onDelete }) {
  const [f, setF] = useState(initial || {
    titulo: "", tipo: "Ads", plataforma: "Reels", conteudo: "",
    link_video: "", client_id: "", status: "Ativo", tags: "",
  });
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving]         = useState(false);
  const [confirming, setConfirming] = useState(false);

  async function save() {
    setSaving(true);
    const payload = { ...f, client_id: f.client_id || null };
    await onSave(payload);
    setSaving(false);
  }

  return (
    <Modal title={initial ? "Editar roteiro" : "Novo roteiro"} onClose={onClose} width={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Título">
          <Inp value={f.titulo} onChange={(e) => up("titulo", e.target.value)} placeholder="Ex: Roteiro Reels — dor de cabeça" />
        </Field>
        <FormRow>
          <Field label="Tipo" half>
            <Sel value={f.tipo} onChange={(e) => up("tipo", e.target.value)}>
              {["Ads","Social"].map((t) => <option key={t}>{t}</option>)}
            </Sel>
          </Field>
          <Field label="Plataforma" half>
            <Sel value={f.plataforma || "Reels"} onChange={(e) => up("plataforma", e.target.value)}>
              {PLATAFORMAS.map((p) => <option key={p}>{p}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <FormRow>
          <Field label="Status" half>
            <Sel value={f.status} onChange={(e) => up("status", e.target.value)}>
              {["Ativo","Estrela","Arquivado"].map((s) => <option key={s}>{s}</option>)}
            </Sel>
          </Field>
          <Field label="Cliente (opcional)" half>
            <Sel value={f.client_id || ""} onChange={(e) => up("client_id", e.target.value)}>
              <option value="">— Geral RC2Clicks —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <Field label="Roteiro / Script">
          <Txta rows={8} value={f.conteudo || ""} onChange={(e) => up("conteudo", e.target.value)}
            placeholder={"HOOK:\n\nCORPO:\n\nCTA:"} />
        </Field>
        <Field label="Link do vídeo gravado (opcional)">
          <Inp value={f.link_video || ""} onChange={(e) => up("link_video", e.target.value)} placeholder="https://drive.google.com/..." />
        </Field>
        <Field label="Tags (separadas por vírgula)">
          <Inp value={f.tags || ""} onChange={(e) => up("tags", e.target.value)} placeholder="clinica, dor, reels, saúde" />
        </Field>
        <Btn variant="accent" full onClick={save} disabled={saving || !f.titulo.trim()}>
          {saving ? "Salvando..." : "Salvar roteiro"}
        </Btn>
        {initial && onDelete && (
          confirming ? (
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="danger" full onClick={onDelete}>Confirmar exclusão</Btn>
              <Btn variant="ghost" full onClick={() => setConfirming(false)}>Cancelar</Btn>
            </div>
          ) : (
            <Btn variant="ghost" full onClick={() => setConfirming(true)} style={{ color: "var(--danger)" }}>
              Excluir roteiro
            </Btn>
          )
        )}
      </div>
    </Modal>
  );
}

export function RoteirosView({ roteiros, clients, onNew, onUpdate, onDelete }) {
  const [tipo,     setTipo]     = useState("Todos");
  const [status,   setStatus]   = useState("Ativo");
  const [search,   setSearch]   = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const visible = roteiros.filter((r) => {
    const q = search.toLowerCase();
    return (
      (tipo   === "Todos" || r.tipo   === tipo) &&
      (status === "Todos" || r.status === status) &&
      (!q || r.titulo?.toLowerCase().includes(q) || r.conteudo?.toLowerCase().includes(q) || r.tags?.toLowerCase().includes(q))
    );
  });

  function statusIcon(s) {
    if (s === "Estrela")   return "★";
    if (s === "Arquivado") return "↓";
    return "●";
  }

  return (
    <div className="view">
      <Stage
        path="roteiros"
        title="Banco de"
        hl="roteiros"
        lead={<>{roteiros.length} roteiros · {roteiros.filter(r => r.status === "Estrela").length} estrelas</>}
        actions={<Btn sm variant="primary" onClick={() => setShowForm(true)}>+ Novo roteiro</Btn>}
      />

      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
        <Inp value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar roteiros..." style={{ maxWidth: 260 }} />
        <Chips label="Tipo" items={["Todos","Ads","Social"].map((t) => ({ id: t, label: t }))} active={tipo} onChange={setTipo} />
        <Chips label="Situação" items={["Todos","Ativo","Estrela","Arquivado"].map((s) => ({ id: s, label: s }))} active={status} onChange={setStatus} />
        <span className="eyebrow" style={{ marginLeft: "auto" }}>{visible.length} resultado(s)</span>
      </div>

      <ExpandTable
        rows={visible}
        minWidth={620}
        empty="Nenhum roteiro encontrado."
        columns={[
          { key: "titulo", label: "Roteiro", render: (r) => (
            <span><span style={{ color: r.status === "Estrela" ? "var(--signature)" : "var(--faint)", marginRight: 8 }}>{statusIcon(r.status)}</span><span className="name">{r.titulo}</span></span>
          ) },
          { key: "tipo", label: "Tipo", render: (r) => [r.tipo, r.plataforma].filter(Boolean).join(" · ") || <span className="sub">—</span> },
          { key: "cliente", label: "Cliente", render: (r) => clients.find((c) => c.id === r.client_id)?.name || <span className="sub">—</span> },
          { key: "status", label: "Situação", render: (r) => r.status ? <Selo tone={ROTEIRO_TONE[r.status] || "off"}>{r.status}</Selo> : null },
        ]}
        detail={(r) => (
          <div className="detail-grid">
            <Detalhe label="Roteiro" pre>{r.conteudo}</Detalhe>
            {r.link_video && (
              <a href={r.link_video} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "var(--primary)" }}>▶ Ver vídeo gravado</a>
            )}
            <Detalhe label="Tags">{r.tags && r.tags.split(",").map((t) => t.trim()).filter(Boolean).map((t) => `#${t}`).join("  ")}</Detalhe>
            <div className="detail-actions">
              <Btn sm variant="secondary" onClick={() => setSelected(r)}>Editar</Btn>
              {r.status !== "Estrela" && <Btn sm variant="ghost" onClick={() => onUpdate(r.id, { status: "Estrela" })}>★ Estrela</Btn>}
              {r.status !== "Arquivado" && <Btn sm variant="ghost" onClick={() => onUpdate(r.id, { status: "Arquivado" })}>Arquivar</Btn>}
              {r.status === "Arquivado" && <Btn sm variant="ghost" onClick={() => onUpdate(r.id, { status: "Ativo" })}>Reativar</Btn>}
            </div>
          </div>
        )}
      />

      {showForm && (
        <RoteiroModal clients={clients} onClose={() => setShowForm(false)} onSave={async (data) => { await onNew(data); setShowForm(false); }} />
      )}
      {selected && (
        <RoteiroModal initial={selected} clients={clients} onClose={() => setSelected(null)}
          onSave={async (data) => { await onUpdate(selected.id, data); setSelected(null); }}
          onDelete={async () => { await onDelete(selected.id); setSelected(null); }} />
      )}
    </div>
  );
}
